import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { visuallyHidden } from '@mui/utils';

import { DataTable, type Column } from '@/components/ui/DataTable';
import { ChangeLine } from '@/components/ui/StatCard';
import { formatCount } from '@/lib/numbers';

import { percentChange } from './change';
import {
  LATER_RELEASE,
  ROWS,
  showsChange,
  tableRows,
  type DashboardData,
  type MetricTableId,
  type RowDefinition,
} from './dashboardData';

interface MetricTableProps {
  table: MetricTableId;
  caption: string;
  /** Absent while loading or after a failure. */
  rows?: DashboardData['rows'];
  /** The previous period's counts, for the rows that show a change. */
  previous?: DashboardData['previousRows'];
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
}

const isAvailable = (id: RowDefinition['id']) =>
  ROWS.find((row) => row.id === id)?.available ?? false;

/**
 * A built row's count: "412 / 173" for a paired row, the plain count
 * otherwise. A pair whose second half is not built shows a dash named for a
 * screen reader (P8). A row whose feature is not built never gets here: it
 * says so in place of a number in every state (P7).
 */
function RowValue({ row, rows }: { row: RowDefinition; rows: DashboardData['rows'] }) {
  const first = formatCount(rows[row.id]);
  if (!row.pairedWith) return <>{first}</>;
  if (isAvailable(row.pairedWith)) {
    return <>{`${first} / ${formatCount(rows[row.pairedWith])}`}</>;
  }
  return (
    <>
      {`${first} / `}
      <span aria-hidden="true">—</span>
      <Box component="span" sx={visuallyHidden}>
        {LATER_RELEASE}
      </Box>
    </>
  );
}

/**
 * One of the dashboard's two tables: fixed metric rows, a count each.
 *
 * While loading, the rows are drawn with a placeholder in place of each
 * number, rather than with the table's own loading panel. The rows are known
 * before the numbers are, and a panel of a different height would make the
 * page jump when they arrive.
 */
export function MetricTable({
  table,
  caption,
  rows,
  previous,
  loading = false,
  error,
  onRetry,
}: MetricTableProps) {
  const columns: Column<RowDefinition>[] = [
    { key: 'metric', header: 'Metric', render: (row) => row.label },
    {
      key: 'count',
      header: 'Count',
      align: 'right',
      render: (row) => {
        return (
          // One width and one line in every state. A paired value is wider
          // than a placeholder, and without this the column widened when the
          // numbers arrived, a label wrapped, and the page grew by a row. The
          // later-release words alone may wrap, so the table keeps its width.
          // They do not wait for the reply, and a row with a change holds a
          // line for it, so the page keeps its height when the numbers land
          // (P35).
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              minWidth: '6.5em',
              whiteSpace: row.available ? 'nowrap' : 'normal',
            }}
          >
            {!row.available ? (
              <Box component="span" data-testid={`dashboard-${table}-later-message`}>
                {LATER_RELEASE}
              </Box>
            ) : loading || !rows ? (
              <>
                <Skeleton width={56} sx={{ ml: 'auto' }} />
                {showsChange(row.id) ? (
                  <Typography variant="body2" component="span" sx={{ display: 'block' }}>
                    <Skeleton width={72} sx={{ ml: 'auto' }} />
                  </Typography>
                ) : null}
              </>
            ) : (
              <>
                <RowValue row={row} rows={rows} />
                {/* Only the rows with a previous show a change (P5). */}
                {previous && showsChange(row.id) ? (
                  <ChangeLine
                    change={percentChange(rows[row.id], previous[row.id])}
                    variant="inline"
                    testId={`dashboard-${table}-change-value`}
                  />
                ) : null}
              </>
            )}
          </Box>
        );
      },
    },
  ];

  // The wireframe heads each table with a title and a one-line subtitle
  // ("Advocacy & Resources" / "Performance by feature"). The caption carries
  // both for a screen reader; sighted readers need them on the page too, or
  // the two tables are told apart only by their rows.
  const [title, subtitle] = splitCaption(caption);

  return (
    <Box>
      <Typography
        variant="h6"
        component="h2"
        data-testid={`dashboard-${table}-title`}
        sx={{ fontWeight: 600 }}
      >
        {title}
      </Typography>
      {subtitle ? (
        <Typography
          variant="body2"
          color="text.secondary"
          data-testid={`dashboard-${table}-caption`}
          sx={{ mb: 1 }}
        >
          {subtitle}
        </Typography>
      ) : null}
      <DataTable
        columns={columns}
        rows={tableRows(table)}
        getRowKey={(row) => row.id}
        caption={caption}
        error={error}
        onRetry={onRetry}
        testId={`dashboard-${table}-table`}
      />
    </Box>
  );
}

/** "Title: subtitle" into its two visible lines; a caption without a colon is all title. */
function splitCaption(caption: string): [string, string | null] {
  const at = caption.indexOf(': ');
  if (at === -1) return [caption, null];
  const subtitle = caption.slice(at + 2);
  return [caption.slice(0, at), subtitle.charAt(0).toUpperCase() + subtitle.slice(1)];
}
